import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { ChevronLeftIcon, ChevronRightIcon, XIcon, TicketIcon, TableIcon } from '@/components/icons';
import { formatTime } from '@/lib/utils';
import type { ComputedEventSchedule } from '@/features/events/types/types';

interface CalendarViewProps {
  schedules: ComputedEventSchedule[];
  businessTimeZone?: string;
  onBookTickets?: (schedule: ComputedEventSchedule) => void;
  onBookTables?: (schedule: ComputedEventSchedule) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({ 
  schedules, 
  businessTimeZone,
  onBookTickets,
  onBookTables
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [showModal, setShowModal] = useState(false);

  const calendarData = useMemo(() => {
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const firstDayOfMonth = new Date(year, month, 1);
    const startingDayOfWeek = firstDayOfMonth.getDay();
    const calendarDates = [];
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - startingDayOfWeek);

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      // Convert schedule times to business timezone for comparison
      const dayEvents = schedules.filter(s => {
        try {
          const scheduleDate = new Date(s.start_time);
          const scheduleDateInBusinessTz = new Date(scheduleDate.toLocaleString('en-US', { timeZone: businessTimeZone }));
          return scheduleDateInBusinessTz.toDateString() === date.toDateString();
        } catch {
          // Fallback to UTC comparison if timezone conversion fails
          return new Date(s.start_time).toDateString() === date.toDateString();
        }
      });
      
      calendarDates.push({
        date,
        events: dayEvents,
        isCurrentMonth: date.getMonth() === month,
        isToday: date.toDateString() === new Date().toDateString()
      });
    }
    return calendarDates;
  }, [schedules, currentDate, businessTimeZone]);

  const monthNames = [
    "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ];
  
  const changeMonth = (offset: number) => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
  };

  const handleDateClick = (date: Date, events: ComputedEventSchedule[]) => {
    if (events.length > 0) {
      setSelectedDay(date);
      setShowModal(true);
    }
  };

  const handleBookTickets = (schedule: ComputedEventSchedule) => {
    setShowModal(false);
    onBookTickets?.(schedule);
  };

  const handleBookTables = (schedule: ComputedEventSchedule) => {
    setShowModal(false);
    onBookTables?.(schedule);
  };

  // Helper function to check if a schedule is in the past
  const isSchedulePast = (schedule: ComputedEventSchedule): boolean => {
    const now = new Date();
    const startTime = new Date(schedule.start_time);
    const durationMs = schedule.duration / 1e6; // Convert nanoseconds to milliseconds
    const endTime = new Date(startTime.getTime() + durationMs);
    return endTime <= now;
  };

  const selectedDayEvents = selectedDay ? 
    calendarData.find(d => d.date.toDateString() === selectedDay.toDateString())?.events || [] : [];

  return (
    <>
      <div className="bg-gray-900/20 border border-gray-800/30 rounded-xl p-8 max-w-4xl mx-auto backdrop-blur-sm">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => changeMonth(-1)} 
            className="p-2 hover:bg-gray-800/30 rounded-md transition-colors"
          >
            <ChevronLeftIcon />
          </button>
          <h3 className="text-lg font-semibold text-gray-200">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          <button 
            onClick={() => changeMonth(1)} 
            className="p-2 hover:bg-gray-800/30 rounded-md transition-colors"
          >
            <ChevronRightIcon />
          </button>
        </div>
        
        <div className="grid grid-cols-7 gap-3 mb-6">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
            <div key={index} className="text-center text-lg text-gray-300 py-4 font-bold">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-3">
          {calendarData.map(({ date, events, isCurrentMonth, isToday }, i) => (
            <button
              key={i}
              onClick={() => handleDateClick(date, events)}
              disabled={!isCurrentMonth || events.length === 0}
              className={`aspect-square flex flex-col items-center justify-start text-sm rounded-xl transition-all duration-300 relative group p-3 hover:scale-105 ${
                !isCurrentMonth 
                  ? 'text-gray-600 cursor-not-allowed'
                  : isToday
                  ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold shadow-xl shadow-blue-500/30 ring-2 ring-blue-400/50'
                  : events.length > 0
                  ? 'bg-gradient-to-br from-gray-800/40 to-gray-700/30 hover:from-gray-700/50 hover:to-gray-600/40 text-gray-200 cursor-pointer hover:text-white border border-gray-700/50 hover:border-gray-600/50'
                  : 'text-gray-600 cursor-not-allowed'
              }`}
            >
              <span className="text-lg font-bold mb-2">{date.getDate()}</span>
              {events.length > 0 && (
                <div className="flex flex-col items-center space-y-1 w-full flex-1 justify-center">
                  {events.slice(0, 2).map((event, idx) => (
                    <div key={idx} className="text-sm bg-gradient-to-r from-blue-500/30 to-blue-600/30 text-blue-200 px-2 py-1 rounded-lg text-center w-full font-medium border border-blue-400/20">
                      {formatTime(event.start_time, businessTimeZone)}
                    </div>
                  ))}
                  {events.length > 2 && (
                    <div className="text-sm text-gray-400 font-semibold">+{events.length - 2}</div>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Booking Modal */}
      <AnimatePresence>
        {showModal && selectedDayEvents.length > 0 && (
          <motion.div 
            className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div 
              className="bg-black/90 border border-gray-800 rounded-2xl w-full max-w-lg shadow-xl"
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-800 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-white">
                    {selectedDay?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </h3>
                  <button 
                    onClick={() => setShowModal(false)}
                    className="p-1 hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <XIcon />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                {selectedDayEvents.map((schedule) => (
                  <div key={schedule.id} className="bg-gray-900 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-white text-sm truncate">{schedule.event?.name || 'Event'}</h4>
                      <span className="text-gray-400 text-sm">
                        {formatTime(schedule.start_time, businessTimeZone)}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {(() => {
                        const isPast = isSchedulePast(schedule);
                        return (
                          <>
                            <button
                              onClick={() => !isPast && handleBookTickets(schedule)}
                              disabled={isPast}
                              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-white text-sm font-medium rounded-lg transition-colors ${
                                isPast 
                                  ? 'bg-gray-900/30 text-gray-500 cursor-not-allowed opacity-60' 
                                  : 'bg-blue-600 hover:bg-blue-500 cursor-pointer'
                              }`}
                            >
                              <TicketIcon />
                              Tickets
                            </button>
                            <button
                              onClick={() => !isPast && handleBookTables(schedule)}
                              disabled={isPast}
                              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-white text-sm font-medium rounded-lg transition-colors ${
                                isPast 
                                  ? 'bg-gray-900/30 text-gray-500 cursor-not-allowed opacity-60' 
                                  : 'bg-gray-700 hover:bg-gray-600 cursor-pointer'
                              }`}
                            >
                              <TableIcon />
                              Tables
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CalendarView;
